/**
 * `navigator.serviceWorker.register()` resolves as soon as the worker script
 * is fetched — on a first visit the worker is still `installing`, and
 * `PushManager.subscribe` (which FCM's `getToken` calls) rejects with
 * "Subscription failed - no active Service Worker" until it activates.
 * Waiting here turns that race into a deterministic order.
 */

export const DEFAULT_SERVICE_WORKER_ACTIVATION_TIMEOUT_MS = 10_000;

export interface ServiceWorkerLike {
  readonly state: ServiceWorkerState;
  addEventListener(type: "statechange", listener: () => void): void;
  removeEventListener(type: "statechange", listener: () => void): void;
}

export interface ServiceWorkerRegistrationLike {
  readonly active: ServiceWorkerLike | null;
  readonly installing: ServiceWorkerLike | null;
  readonly waiting: ServiceWorkerLike | null;
}

/**
 * Resolves once the registration has an active worker; rejects if the worker
 * becomes redundant or the timeout elapses (a hung install must not block
 * token registration forever — the caller's retry loop reports it).
 */
export const waitForActiveServiceWorker = (
  registration: ServiceWorkerRegistrationLike,
  timeoutMs: number = DEFAULT_SERVICE_WORKER_ACTIVATION_TIMEOUT_MS,
): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (registration.active) {
      resolve();
      return;
    }

    const worker = registration.installing ?? registration.waiting;
    if (!worker) {
      reject(
        new Error(
          "Service worker registration has no installing, waiting or active worker",
        ),
      );
      return;
    }

    const onStateChange = () => {
      if (worker.state === "activated") {
        cleanup();
        resolve();
      } else if (worker.state === "redundant") {
        cleanup();
        reject(new Error("Service worker became redundant before activating"));
      }
    };

    const cleanup = () => {
      worker.removeEventListener("statechange", onStateChange);
      clearTimeout(timer);
    };

    const timer = setTimeout(() => {
      cleanup();
      reject(
        new Error(`Service worker did not activate within ${timeoutMs}ms`),
      );
    }, timeoutMs);

    worker.addEventListener("statechange", onStateChange);

    // The state may already be terminal by the time the listener is attached.
    onStateChange();
  });
